import { useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type { MatchPublic, PredictionPublic, StadiumPublic } from '@mondy/shared';
import { api } from '@/api';
import { ApiError } from '@mondy/shared/api-client';
import { useNavHistory } from '@/state/navHistory';
import { MatchDayList } from '@/components/MatchDayList';
import { Spinner } from '@/components/Spinner';
import { useCurrentUser } from '@/hooks/useCurrentUser';

interface StadiumResponse {
  stadium: StadiumPublic;
}

interface MatchListResponse {
  matches: MatchPublic[];
}

export function StadiumDetailPage() {
  const { goBack } = useNavHistory();
  const { id } = useParams<{ id: string }>();
  const user = useCurrentUser();

  const query = useQuery({
    queryKey: ['stadium', id],
    queryFn: () => api.get<StadiumResponse>(`/api/stadiums/${id}`),
    enabled: Boolean(id),
    staleTime: 60 * 60_000,
  });

  const matchesQuery = useQuery({
    queryKey: ['matches', { stadiumId: id }],
    queryFn: () => api.get<MatchListResponse>(`/api/matches?stadiumId=${id}`),
    enabled: Boolean(id),
  });

  const predictionsQuery = useQuery({
    queryKey: ['predictions', 'mine'],
    queryFn: () => api.get<{ predictions: PredictionPublic[] }>('/api/predictions/mine'),
    enabled: !!user,
  });

  const predictionsByMatch = useMemo(() => {
    const map = new Map<string, PredictionPublic>();
    if (predictionsQuery.data) {
      for (const p of predictionsQuery.data.predictions) {
        map.set(p.matchId, p);
      }
    }
    return map;
  }, [predictionsQuery.data]);

  if (query.isLoading) {
    return <Spinner fullScreen label="Loading stadium" />;
  }
  if (query.isError) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    return (
      <div style={{ textAlign: 'center', padding: '64px 0' }}>
        <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          {notFound ? 'Stadium not found' : 'Something went wrong'}
        </p>
        <Link to="/stadiums" className="btn btn-ghost">
          Back to stadiums
        </Link>
      </div>
    );
  }

  const { stadium } = query.data!;

  return (
    <>
      <div className="page-head">
        <div className="page-title">
          <div className="k">
            <button
              type="button"
              onClick={() => goBack('/stadiums')}
              style={{
                color: 'inherit',
                textDecoration: 'none',
                marginRight: 8,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                font: 'inherit',
                padding: 0,
              }}
            >
              ←
            </button>
            {stadium.city ? `${stadium.city}, ${stadium.country}` : stadium.country}
          </div>
          <h1>{stadium.name}</h1>
        </div>
      </div>

      {stadium.imageUrl ? (
        <div
          className="stadium-photo"
          style={{
            aspectRatio: '16 / 9',
            maxHeight: 360,
            borderRadius: 16,
            border: '1px solid var(--line)',
            backgroundImage: `url(${stadium.imageUrl})`,
            marginBottom: 20,
          }}
        />
      ) : null}

      <section className="mb-8 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Capacity" value={stadium.capacity ? stadium.capacity.toLocaleString() : '—'} />
        <Stat label="Opened" value={stadium.constructionYear ? String(stadium.constructionYear) : '—'} />
        <Stat label="Matches" value={String(stadium.matchCount)} />
      </section>

      {stadium.fact ? (
        <section className="mb-8">
          <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
            Did you know
          </h2>
          <p
            className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-4 text-sm leading-relaxed"
          >
            {stadium.fact}
          </p>
        </section>
      ) : null}

      <section className="mb-8 flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[color:var(--color-fg-muted)]">
          Matches
        </h2>
        {matchesQuery.isLoading ? (
          <Spinner label="Loading matches" />
        ) : matchesQuery.isError ? (
          <p className="py-6 text-center text-sm text-[color:var(--danger)]">
            Couldn't load matches. Try again in a moment.
          </p>
        ) : (matchesQuery.data?.matches.length ?? 0) === 0 ? (
          <p className="py-6 text-center text-sm text-[color:var(--color-fg-muted)]">
            No matches scheduled here yet.
          </p>
        ) : (
          <MatchDayList
            matches={matchesQuery.data!.matches}
            predictionsByMatch={predictionsByMatch}
          />
        )}
      </section>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-3">
      <div className="text-xs uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        {label}
      </div>
      <div className="bebas" style={{ fontSize: 26, letterSpacing: '0.02em' }}>
        {value}
      </div>
    </div>
  );
}
